import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font,
  pdf,
} from "@react-pdf/renderer";
import {
  TIPO_FOTO_LABELS,
  CLASSIFICACAO_PORTE_LABELS,
  VEICULO_TIPO_LABELS,
  CORES,
} from "./constants";

// ── Font registration ──────────────────────────────────────────────────────
Font.register({
  family: "Inter",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/npm/@fontsource/inter@5/files/inter-latin-400-normal.woff",
      fontWeight: 400,
    },
    {
      src: "https://cdn.jsdelivr.net/npm/@fontsource/inter@5/files/inter-latin-700-normal.woff",
      fontWeight: 700,
    },
  ],
});

// ── Color theme ────────────────────────────────────────────────────────────
const C = {
  primary: "#1E3A5F",
  accent: "#2563EB",
  success: "#059669",
  danger: "#DC2626",
  warning: "#D97706",
  textDark: "#1A1A2E",
  textMid: "#4A5568",
  textLight: "#718096",
  bgLight: "#F7F8FA",
  borderGray: "#E2E8F0",
  white: "#FFFFFF",
} as const;

// ── Types ──────────────────────────────────────────────────────────────────
export interface LaudoAvaliacaoFotoPdf {
  id: string;
  storageUrl: string;
  tipoFoto: string;
}

export interface DesmonteSugeridoPdf {
  nome: string;
  telefone: string | null;
  endereco: string;
}

export interface LaudoAvaliacaoWithDetails {
  id: string;
  codigo?: string | null;
  status: string;
  criadoEm: string | Date;
  valorDesejado?: string | number | null;
  veiculo?: {
    placa: string;
    chassi?: string | null;
    marca: string;
    modelo: string;
    anoFabricacao?: number | null;
    tipoVeiculo: string;
    cor?: string | null;
    valorFipe?: string | number | null;
    classificacaoPorte?: string | null;
  } | null;
  fotos: LaudoAvaliacaoFotoPdf[];
  sinistroLocalizacao?: {
    endereco: string;
    desmontes: DesmonteSugeridoPdf[];
  } | null;
}

// ── StyleSheet ─────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: "Inter",
    fontSize: 10,
    paddingLeft: 32,
    paddingRight: 32,
    paddingTop: 28,
    paddingBottom: 28,
    backgroundColor: C.white,
    color: C.textDark,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: C.primary,
  },
  headerTitle: { fontSize: 18, fontWeight: 700, color: C.primary, flex: 1 },
  headerSubtitle: { fontSize: 10, color: C.textMid },
  section: { marginBottom: 10 },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: C.primary,
    marginBottom: 5,
    paddingBottom: 3,
    borderBottomWidth: 1,
    borderBottomColor: C.borderGray,
  },
  infoRow: { flexDirection: "row", marginBottom: 3 },
  infoLabel: { fontSize: 10, color: C.textLight, width: 90 },
  infoValue: { fontSize: 10, color: C.textDark, flex: 1 },
  valorBox: {
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: C.accent,
    borderRadius: 4,
    padding: 8,
    marginTop: 14,
    flexDirection: "row",
    alignItems: "center",
  },
  valorBoxBar: {
    width: 4,
    height: 32,
    backgroundColor: C.accent,
    marginRight: 10,
    borderRadius: 2,
  },
  valorBoxLabel: { fontSize: 7, fontWeight: 700, color: C.accent, marginBottom: 2 },
  valorBoxValue: { fontSize: 13, fontWeight: 700, color: C.textDark },
  fipeBox: {
    backgroundColor: "#FFFBEB",
    borderWidth: 1,
    borderColor: C.warning,
    borderRadius: 4,
    padding: 8,
    marginTop: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  fipeBoxBar: {
    width: 4,
    height: 32,
    backgroundColor: C.warning,
    marginRight: 10,
    borderRadius: 2,
  },
  fipeBoxLabel: { fontSize: 7, fontWeight: 700, color: C.warning, marginBottom: 2 },
  fipeBoxValue: { fontSize: 13, fontWeight: 700, color: C.textDark },
  photoGrid: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 4 },
  photoBlock: { marginTop: 4, marginBottom: 2 },
  photoLabel: {
    fontSize: 8,
    fontWeight: 700,
    color: C.textMid,
    marginBottom: 2,
  },
  photoThumb: {
    width: 122,
    height: 92,
    objectFit: "cover",
    borderWidth: 0.5,
    borderColor: C.borderGray,
  },
  footer: {
    position: "absolute",
    bottom: 16,
    left: 32,
    right: 32,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 0.5,
    borderTopColor: C.borderGray,
    paddingTop: 4,
  },
  footerText: { fontSize: 8, color: C.textLight },
});

// ── Helpers ────────────────────────────────────────────────────────────────
function formatDate(d: string | Date): string {
  return new Date(d).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatBRL(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatCor(cor: string | null | undefined): string {
  if (!cor) return "—";
  const match = CORES.find((c) => c.nome === cor);
  return match ? `${match.codigo} - ${match.nome}` : cor;
}

// ── Main component ─────────────────────────────────────────────────────────
export function LaudoAvaliacaoPdf({
  laudo,
}: {
  laudo: LaudoAvaliacaoWithDetails;
}): React.ReactElement {
  const { veiculo, fotos = [] } = laudo;

  return (
    <Document
      title={`Laudo de Avaliação — ${veiculo?.placa ?? "sem-placa"}`}
      author="ReMonta"
    >
      <Page size="A4" style={s.page}>
        {/* Header */}
        <View style={s.headerRow} fixed>
          <Text style={s.headerTitle}>Laudo de Avaliação</Text>
          <View>
            <Text style={s.headerSubtitle}>ReMonta</Text>
            <Text style={s.headerSubtitle}>
              Laudo #{(laudo.codigo ?? laudo.id).toString().slice(0, 8)} ·{" "}
              {formatDate(laudo.criadoEm)}
            </Text>
          </View>
        </View>

        {/* Dados do Veículo */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Dados do Veículo</Text>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Placa</Text>
            <Text style={s.infoValue}>{veiculo?.placa ?? "—"}</Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Chassi</Text>
            <Text style={s.infoValue}>{veiculo?.chassi ?? "—"}</Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Marca / Modelo</Text>
            <Text style={s.infoValue}>
              {veiculo?.marca} {veiculo?.modelo} · {veiculo?.anoFabricacao ?? "—"}
            </Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Tipo</Text>
            <Text style={s.infoValue}>
              {(veiculo?.tipoVeiculo &&
                VEICULO_TIPO_LABELS[veiculo.tipoVeiculo]) ??
                veiculo?.tipoVeiculo ??
                "—"}
            </Text>
          </View>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Cor</Text>
            <Text style={s.infoValue}>{formatCor(veiculo?.cor)}</Text>
          </View>
        </View>

        {/* Avaliação */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Avaliação</Text>
          <View style={s.infoRow}>
            <Text style={s.infoLabel}>Classificação</Text>
            <Text style={s.infoValue}>
              {(veiculo?.classificacaoPorte &&
                CLASSIFICACAO_PORTE_LABELS[veiculo.classificacaoPorte]) ??
                "—"}
            </Text>
          </View>
          <View style={s.fipeBox}>
            <View style={s.fipeBoxBar} />
            <View>
              <Text style={s.fipeBoxLabel}>VALOR FIPE</Text>
              <Text style={s.fipeBoxValue}>R$ {formatBRL(veiculo?.valorFipe)}</Text>
            </View>
          </View>
          <View style={s.valorBox}>
            <View style={s.valorBoxBar} />
            <View>
              <Text style={s.valorBoxLabel}>VALOR DESEJADO</Text>
              <Text style={s.valorBoxValue}>R$ {formatBRL(laudo.valorDesejado)}</Text>
            </View>
          </View>
        </View>

        {laudo.sinistroLocalizacao && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Local do Sinistro</Text>
            <Text style={s.infoValue}>
              {laudo.sinistroLocalizacao.endereco}
            </Text>
            <Text style={[s.sectionTitle, { marginTop: 10 }]}>
              Desmontes sugeridos em um raio de 100km
            </Text>
            {laudo.sinistroLocalizacao.desmontes.length === 0 ? (
              <Text style={s.infoValue}>
                Nenhum desmonte encontrado no raio de 100km.
              </Text>
            ) : (
              laudo.sinistroLocalizacao.desmontes.map((desmonte, index) => (
                <Text key={index} style={s.infoValue}>
                  {`D${index + 1} | ${desmonte.endereco} | ${
                    desmonte.telefone ?? "sem telefone"
                  } - ${desmonte.nome}`}
                </Text>
              ))
            )}
          </View>
        )}

        {/* Registro Fotográfico */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Registro Fotográfico do Bem Avaliado</Text>
          <View style={s.photoGrid}>
            {fotos.map((foto) => (
              <View key={foto.id} style={s.photoBlock}>
                <Text style={s.photoLabel}>
                  {TIPO_FOTO_LABELS[foto.tipoFoto] ?? foto.tipoFoto}
                </Text>
                <Image src={foto.storageUrl} style={s.photoThumb} />
              </View>
            ))}
          </View>
          {fotos.length === 0 && (
            <Text style={{ fontSize: 9, color: C.textLight, marginTop: 4 }}>
              Nenhuma foto registrada.
            </Text>
          )}
        </View>

        {/* Footer */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>ReMonta — Laudo de Avaliação</Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}

export { pdf };
